/// Text builders for spontaneous (non-command) ATC readbacks. Each
/// helper takes the aircraft state at the moment of the event and
/// returns the one-line "FOA101, ..." string the readback toast
/// stack should display.
///
/// Kept separate from the autopilot so the strings can be tweaked
/// without touching simulation code, and tested independently.

import type { Aircraft, EmergencyReason } from './Aircraft'
import { describeGoal } from '../types/LevelData'

/// Initial-contact readback the aircraft sends on spawn. Variant by
/// status + goal type so the player gets useful context immediately.
export function spawnCheckInText(aircraft: Aircraft): string {
  const cs = aircraft.flightNumber
  if (aircraft.status === 'awaitingTakeoff') {
    const dep = aircraft.departureRunway
    const wp = aircraft.goal.takeoff?.waypointName
    const where = dep ? `runway ${dep.runwayId}` : 'runway'
    return wp
      ? `${cs}, holding short ${where}, departure ${wp}`
      : `${cs}, holding short ${where}`
  }
  const alt = aircraft.altitudeFt.toLocaleString()
  if (aircraft.goal.land) {
    return `${cs}, ${alt} ft, inbound ${aircraft.goal.land.airportName}`
  }
  if (aircraft.goal.transition) {
    return `${cs}, ${alt} ft, transitioning via ${aircraft.goal.transition.waypointName}`
  }
  if (aircraft.goal.takeoff) {
    return `${cs}, ${alt} ft, direct ${aircraft.goal.takeoff.waypointName}`
  }
  return `${cs}, with you, ${alt} ft, ${describeGoal(aircraft.goal)}`
}

/// Aircraft reaches a player-issued direct-to fix.
export function crossingFixText(aircraft: Aircraft, fixName: string): string {
  return `${aircraft.flightNumber}, crossing ${fixName}`
}

/// Aircraft converges on its assigned altitude (climb or descent
/// completes). Skipped during landing/takeoff phases.
export function levelOffText(aircraft: Aircraft): string {
  return `${aircraft.flightNumber}, level ${aircraft.altitudeFt.toLocaleString()}`
}

/// Aircraft establishes on the localizer the first time
/// `tickApproachIntercept` finds itself within the on-localizer
/// band. Subsequent oscillations don't re-fire.
export function establishedText(aircraft: Aircraft, runwayId: string): string {
  return `${aircraft.flightNumber}, established RWY ${runwayId}`
}

/// Aircraft declares an emergency at the moment one is rolled (right
/// after spawn for landing-goal aircraft on Trainee+ difficulty).
export function emergencyDeclareText(
  aircraft: Aircraft,
  reason: EmergencyReason,
): string {
  return `Mayday, mayday, mayday — ${aircraft.flightNumber}, ${emergencyPhrase(reason)}`
}

function emergencyPhrase(reason: EmergencyReason): string {
  switch (reason) {
    case 'birdStrike':
      return 'bird strike, request priority'
    case 'lowFuel':
      return 'minimum fuel, request priority'
    case 'engineFailure':
      return 'engine failure, request immediate landing'
    case 'vfrInIfr':
      return 'VFR in IMC, request vectors'
    case 'medicalEmergency':
      return 'medical emergency on board, request priority'
    case 'pressureLoss':
      return 'pressure loss, descending, request priority'
  }
}
